// Utility functions for settings management
import { settingsService, getSettingValue } from '../services/settingsService';
import { SettingKey, DEFAULT_SETTINGS } from '../types/settings';

/**
 * Get setting value with fallback to default
 * This is a synchronous version that uses cached values
 */
export function getCachedSettingValue<T = any>(
  settings: Record<string, any>,
  key: SettingKey,
  defaultValue?: T
): T {
  const value = settings[key];
  return value !== undefined 
    ? value as T 
    : (defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T;
}

/**
 * Validate setting value based on expected type
 */
export function validateSettingValue(dataType: string, value: any): boolean {
  switch (dataType) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number' && !isNaN(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'array':
      return Array.isArray(value);
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    default:
      return false;
  }
}

/**
 * Parse setting value from string (for form inputs)
 */
export function parseSettingValue(dataType: string, stringValue: string): any {
  switch (dataType) {
    case 'string':
      return stringValue;
    case 'number':
      const num = parseFloat(stringValue);
      return isNaN(num) ? 0 : num;
    case 'boolean':
      return stringValue.toLowerCase() === 'true';
    case 'array':
      try {
        const parsed = JSON.parse(stringValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    case 'object':
      try {
        const parsed = JSON.parse(stringValue);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) 
          ? parsed 
          : {};
      } catch {
        return {};
      }
    default:
      return stringValue;
  }
}

/**
 * Format setting value for display
 */
export function formatSettingValue(value: any, dataType: string): string {
  switch (dataType) {
    case 'string':
      return value.toString();
    case 'number':
      return value.toString();
    case 'boolean':
      return value ? 'true' : 'false';
    case 'array':
    case 'object':
      return JSON.stringify(value, null, 2);
    default:
      return value.toString();
  }
}

/**
 * Get readable category name
 */
export function getCategoryDisplayName(category: string): string {
  const categoryNames: Record<string, string> = {
    limits: 'Reading Limits',
    admin: 'Admin Configuration',
    features: 'Feature Flags',
    ui: 'User Interface',
    performance: 'Performance',
  };
  
  return categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Check if a setting is sensitive (should not be displayed to non-admins)
 */
export function isSensitiveSetting(key: string): boolean {
  const sensitiveSettings = [
    'admin_users',
    'super_admin_users',
    'debug_mode',
    'cache_settings_duration',
    'max_api_requests_per_minute',
  ];
  
  return sensitiveSettings.includes(key);
}

/**
 * Get default value for a setting based on its data type
 */
export function getDefaultValueForType(dataType: string): any {
  switch (dataType) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return null;
  }
}

/**
 * Batch load multiple settings
 */
export async function batchLoadSettings(
  keys: SettingKey[],
  fid?: string
): Promise<Record<string, any>> {
  const results: Record<string, any> = {};
  
  // Load all settings in parallel
  const promises = keys.map(async (key) => {
    try {
      const value = await getSettingValue(key, DEFAULT_SETTINGS[key], fid);
      return { key, value };
    } catch (error) {
      console.error(`Error loading setting ${key}:`, error);
      return { key, value: DEFAULT_SETTINGS[key] };
    }
  });
  
  const settingResults = await Promise.all(promises);
  
  settingResults.forEach(({ key, value }) => {
    results[key] = value;
  });
  
  return results;
}

/**
 * Create a settings context value for React context
 */
export function createSettingsContextValue(
  settings: Record<string, any>,
  updateSetting?: (key: string, value: any) => Promise<boolean>
) {
  return {
    settings,
    getSetting: <T = any>(key: SettingKey, defaultValue?: T): T => 
      getCachedSettingValue(settings, key, defaultValue),
    updateSetting: updateSetting || (() => Promise.resolve(false)),
    isLoaded: Object.keys(settings).length > 0,
  };
}

/**
 * Merge settings with defaults to ensure all required settings are present
 */
export function mergeWithDefaults(settings: Record<string, any>): Record<string, any> {
  const merged = { ...DEFAULT_SETTINGS };
  
  Object.keys(settings).forEach(key => {
    if (settings[key] !== undefined) {
      merged[key as SettingKey] = settings[key];
    }
  });
  
  return merged;
}

/**
 * Check if settings object is valid
 */
export function validateSettingsObject(settings: Record<string, any>): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  // Check if essential settings exist
  const essentialSettings: SettingKey[] = [
    'daily_reading_limit',
    'additional_reading_limit',
    'max_total_daily_readings',
  ];
  
  essentialSettings.forEach(key => {
    if (settings[key] === undefined) {
      errors.push(`Missing essential setting: ${key}`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}